import React, { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const About: React.FC = () => {
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    gsap.utils.toArray('.about-card').forEach((card: any, index) => {
      gsap.fromTo(card, 
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: index * 0.2,
          scrollTrigger: {
            trigger: card,
            start: 'top 80%',
            end: 'bottom 20%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="section-header animate-on-scroll">
          <h2 className="heading-lg heading-on-light mb-6">
            About Maho Fine Foods
          </h2>
          <p className="text-body-lg text-on-light max-w-3xl mx-auto">
            We are the premier network connecting exceptional food suppliers with discerning culinary establishments, 
            ensuring the highest quality ingredients reach your kitchen.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div className="about-card card-base">
            <div className="card-content-lg">
              <h3 className="heading-md heading-on-light mb-6">
                Connecting Culinary Excellence
            </h3>
              <p className="text-body text-on-light mb-6">
              At Maho Fine Foods, we believe that exceptional dining experiences start with exceptional ingredients. 
                Our carefully curated network of premium suppliers ensures that restaurants, hotels, and culinary establishments 
                have access to the finest products from around the world.
            </p>
              <p className="text-body text-on-light mb-8">
                With years of experience in the food industry, we understand the unique challenges that culinary professionals face. 
                That's why we've built relationships with trusted suppliers who share our commitment to quality, sustainability, and innovation.
            </p>
            <a href="#services" className="btn-primary">
              Learn More About Our Services
            </a>
          </div>
          </div>
          <div className="about-card">
              <img 
                src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="Premium ingredients and fine dining" 
              className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          <div className="about-card text-center p-8 bg-background border border-accent rounded-lg hover:shadow-lg transition-all duration-300">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="heading-xs heading-on-light text-accent-bold text-on-light mb-4">Quality Assurance</h4>
            <p className="text-body-sm text-on-light">
              Every supplier in our network undergoes rigorous quality checks to ensure they meet our 
              high standards for food safety and excellence.
            </p>
          </div>

          <div className="about-card text-center p-8 bg-background border border-accent rounded-lg hover:shadow-lg transition-all duration-300">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h4 className="text-xl font-semibold text-text mb-4 roboto-bold">Expert Network</h4>
            <p className="text-text-accent roboto-regular">
              Our team of culinary experts and food industry professionals carefully vets each supplier 
              to maintain the integrity of our network.
            </p>
          </div>

          <div className="about-card text-center p-8 bg-background border border-accent rounded-lg hover:shadow-lg transition-all duration-300">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-xl font-semibold text-text mb-4 roboto-bold">Fast Delivery</h4>
            <p className="text-text-accent roboto-regular">
              Our efficient logistics network ensures timely delivery of fresh ingredients directly 
              to your establishment when you need them.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
